/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { forwardRef, useCallback, useMemo } from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import { __ } from '@web-stories-wp/i18n';

/**
 * Internal dependencies
 */
import stripHTML from '../../../../utils/stripHTML';
import { FontsService, useFont, isCustomFont } from '../../../../app/font';
import { MULTIPLE_DISPLAY_VALUE, MULTIPLE_VALUE } from '../../../../constants';
import { AdvancedDropDown } from '../../../form';
import { Option, Selected } from '../../../form/advancedDropDown/list/styled';
import getElementWideFontUpdate from './getElementWideFontUpdate';
import getSelectedFont from './getSelectedFont';
import { maybeLoadFont } from '../../../../app/font/actions/useLoadFontFiles';

const FontPicker = forwardRef(function FontPicker(
  {
    selectedElements,
    pushUpdate,
    highlightStylesOverride,
    fontFamily,
    fontWeight,
    isItalic,
    isEditingMixedTypography,
    handleSetFontFamily,
  },
  ref
) {
  const fontStyle = isItalic ? 'italic' : 'normal';

  const {
    fonts = [],
    recentFonts = [],
    curatedFonts = [],
    customFonts = [],
    addRecentFont,
    maybeEnqueueFontStyle,
    ensureMenuFontsLoaded,
  } = useFont(
    ({
      actions: { addRecentFont, ensureMenuFontsLoaded, maybeEnqueueFontStyle },
      state: { fonts, recentFonts, curatedFonts, customFonts },
    }) => ({
      addRecentFont,
      ensureMenuFontsLoaded,
      maybeEnqueueFontStyle,
      recentFonts,
      curatedFonts,
      customFonts,
      fonts,
    })
  );

  const handleFontPickerChange = useCallback(
    async ({ id }) => {
      const fontObj = fonts.find((item) => item.value === id);
      const newFont = getSelectedFont(fontObj, id);

      if (isEditingMixedTypography) {
        handleSetFontFamily(newFont);
        addRecentFont(fontObj);
        await maybeEnqueueFontStyle(
          selectedElements.map(({ content }) => ({
            font: newFont,
            fontStyle,
            fontWeight: fontWeight === MULTIPLE_VALUE ? undefined : fontWeight,
            content: stripHTML(content),
          }))
        );
        return;
      }

      await maybeEnqueueFontStyle(
        selectedElements.map(({ content }) => {
          return {
            font: newFont,
            fontStyle,
            fontWeight,
            content: stripHTML(content),
          };
        })
      );
      addRecentFont(fontObj);
      // If the element was previously copied from Figma, we can get rid of the figmaFontName now
      // (this will allow us to calculate the right margns in elementWithTextParagraphStyle)
      pushUpdate((element) => getElementWideFontUpdate(element, newFont), true);
    },
    [
      addRecentFont,
      fontStyle,
      fontWeight,
      fonts,
      maybeEnqueueFontStyle,
      pushUpdate,
      selectedElements,
      isEditingMixedTypography,
      handleSetFontFamily,
    ]
  );

  const fontMap = useMemo(() => {
    const map = new Map();
    // curatedFonts and recentFonts are subsets of fonts.
    fonts.forEach((f) => {
      map.set(f.id, f);
    });
    return map;
  }, [fonts]);

  const onObserve = (observedFonts) => {
    const googleFonts = observedFonts.filter(
      (fontName) => fontMap.get(fontName)?.service === FontsService.google
    );

    ensureMenuFontsLoaded(googleFonts);

    for (const fontName of observedFonts) {
      const font = fontMap.get(fontName);
      if (font && isCustomFont(font)) {
        maybeLoadFont(font);
      }
    }
  };

  const renderer = ({ option, ...rest }, _ref) => {
    return (
      <Option
        ref={_ref}
        {...rest}
        fontFamily={
          typeof option.service === 'string' &&
          option.service.includes('google')
            ? `'${option.name}::MENU'`
            : option.name
        }
      >
        {fontFamily === option.id && (
          <Selected aria-label={__('Selected', 'web-stories')} />
        )}
        {option.name}
      </Option>
    );
  };

  const priorityOptionGroups = useMemo(() => {
    return [
      ...(customFonts?.length
        ? [
            {
              label: __('Your fonts', 'web-stories'),
              options: customFonts,
            },
          ]
        : []),
      ...(recentFonts?.length
        ? [
            {
              label: __('Recently used', 'web-stories'),
              options: recentFonts,
            },
          ]
        : []),
    ];
  }, [customFonts, recentFonts]);

  return (
    <AdvancedDropDown
      ref={ref}
      highlightStylesOverride={highlightStylesOverride}
      data-testid="font"
      aria-label={__('Font family', 'web-stories')}
      options={fonts}
      primaryOptions={curatedFonts}
      primaryLabel={__('Recommended', 'web-stories')}
      priorityOptionGroups={priorityOptionGroups}
      searchResultsLabel={__('Search results', 'web-stories')}
      selectedId={MULTIPLE_VALUE === fontFamily ? '' : fontFamily}
      placeholder={
        MULTIPLE_VALUE === fontFamily ? MULTIPLE_DISPLAY_VALUE : fontFamily
      }
      hasSearch
      onChange={handleFontPickerChange}
      onObserve={onObserve}
      renderer={forwardRef(renderer)}
      disabled={!fonts?.length}
      dropDownLabel={__('Font', 'web-stories')}
    />
  );
});

FontPicker.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
  highlightStylesOverride: PropTypes.array,
  fontFamily: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isItalic: PropTypes.bool,
  isEditingMixedTypography: PropTypes.bool,
  handleSetFontFamily: PropTypes.func,
};

export default FontPicker;
